import React from "react";
import { styled } from "sharedUI/styles";
import { OrgIcon } from "designer/apps/orgs/orgs-sidebar/org-icon";
import { RootState } from "store/types";
import { trackEvent } from "analytics";
import { UserData } from "common/Database";
import { getCurrentTermsOfService } from "common/terms-of-service";
import { getBuilderUserDatabase } from "lib/web-database";
import { dontAwait } from "@glideapps/ts-necessities";
import { getOrgMembers, getOrgOwner, OrgMember } from "designer/apps/orgs/orgs-lib";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { GlideFC } from "lib/glide-fc";
import { useBuilderSelector } from "hooks/use-builder-selector";
import { ButtonVNext } from "designer/ui/smart/button-vnext/button-vnext";

interface AcceptTeamInviteProps {
    onDone: () => void;
    orgID: string;
    userData: UserData;
}

const AcceptTeamInviteStyle = styled.div`
    a {
        text-decoration: underline !important;
    }

    .accept-and-continue {
        margin-top: 14px;
    }
`;

const AcceptTeamInvite: GlideFC<AcceptTeamInviteProps> = ({ orgID, onDone, userData }) => {
    const orgs = useBuilderSelector((state: RootState) => state.organizations);
    const [termsLink, setTermsLink] = React.useState<string>("");
    const [privacyLink, setPrivacyLink] = React.useState<string>("");
    const [orgMembers, setOrgMembers] = React.useState<readonly OrgMember[] | undefined>(undefined);

    React.useEffect(() => {
        const fn = async () => {
            const result = await getCurrentTermsOfService(getBuilderUserDatabase(userData.id));
            result?.termsLink && setTermsLink(result?.termsLink);
            result?.privacyLink && setPrivacyLink(result?.privacyLink);
        };
        dontAwait(fn());
    }, [userData.id]);

    const org = orgs.find(o => o.id === orgID);
    const orgDisplayName = org?.displayName || "";

    React.useEffect(() => {
        const fn = async () => {
            const members = await getOrgMembers(orgID);
            if (members !== undefined) {
                setOrgMembers(members);
            }
        };
        dontAwait(fn());
    }, [orgID]);

    const owner = orgMembers && getOrgOwner(orgMembers);

    if (orgMembers === undefined || termsLink === "" || privacyLink === "" || owner === undefined || orgs === []) {
        return <LoadingIndicator />;
    }

    return (
        <AcceptTeamInviteStyle>
            <hgroup>
                <OrgIcon size={"xlarge"} label={orgDisplayName} />
                <h1>{`Accept ${owner?.displayName || ""}${
                    owner?.displayName ? "'s " : ""
                }invite to ${orgDisplayName}`}</h1>
            </hgroup>

            <p>
                <small>You will be able to create and edit apps, and invite other team members to collaborate.</small>
            </p>

            <p>
                <small>
                    By continuing, you agree to our{" "}
                    <a href={termsLink} title="Terms of Service" target="_blank" rel="noopener noreferrer">
                        Terms
                    </a>{" "}
                    and{" "}
                    <a href={privacyLink} title="Privacy Policy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                    </a>
                    .
                </small>
            </p>

            <ButtonVNext
                onClick={() => {
                    trackEvent("sign up accept tos", {});
                    onDone();
                }}
                buttonType={"primary"}
                variant="default"
                size="xlg"
                label="Accept and Continue"
                isFullWidth={true}
                className={"accept-and-continue"}
            />
        </AcceptTeamInviteStyle>
    );
};

export default AcceptTeamInvite;
